import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';

// Utils
import {
    getMobileInboxAutomaticOpenTimestamp,
    getElementId,
} from '../../../../common/elements/utils/elementPropertyUtils';

// Actions
import { updateElement } from '../../../element/actions/elementActions';

// Types
import { ImMNElement } from '../../../../common/elements/elementModelTypes';

/**
 * Hook that checks if the user is on ios mobile and navigating to a board or loads the app
 * with elements created from the iOS Share Extension. If so, it opens the inbox sheet
 * based on the mobileInboxAutomaticOpenTimestamp property and clears the property
 * from the board once the sheet is opened.
 * This ensures that the user doesnt miss seeing the elements created from the iOS Share Extension
 */
const useInboxManageAutomaticOpen = (
    currentBoard: ImMNElement,
    inboxElementIds: string[],
    isSheetOpen: boolean,
    openInboxSheet: Function,
) => {
    const dispatch = useDispatch();
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }

        if (isSheetOpen) return;

        if (inboxElementIds.length === 0) return;

        // Skip if there are no newly added elements
        if (!getMobileInboxAutomaticOpenTimestamp(currentBoard)) return;

        timeoutRef.current = setTimeout(() => {
            openInboxSheet();
            dispatch(
                updateElement({
                    id: getElementId(currentBoard),
                    changes: {
                        mobileInboxAutomaticOpenTimestamp: null,
                    },
                    sync: true,
                }),
            );
        }, 150);

        // Clean up the timer when the component unmounts
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
        };
    }, [currentBoard, inboxElementIds]);

    return null;
};

export default useInboxManageAutomaticOpen;
